import React from 'react';
import { Check, ChevronDown, Copy, Plus } from 'lucide-react';
import styled from '../../styled';
import { COLORS } from '../../themes/color';
import {
  AppMockupChrome,
  ProductMockupBody,
  ProductMockupFrame
} from '../AmplifyProductMockups/shared';

const TitleRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 12px;
  margin-bottom: 14px;
  border-bottom: 1px solid #f1f5f9;
  min-width: 0;
`;

const TitleIcon = styled('div')`
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 5px;
  background: #171717;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TitleText = styled('div')`
  flex: 1;
  font-size: 13px;
  font-weight: 700;
  color: #0e1c2b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
`;

const StepRow = styled('div')`
  display: flex;
  gap: 10px;
  align-items: flex-start;
`;

const StepBadge = styled('div')<{ completed?: boolean }>`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: ${(p) => (p.completed ? '#94a3b8' : COLORS.PRIMARY)};
  font-size: 11px;
  font-weight: 700;
`;

const StepContent = styled('div')`
  flex: 1;
  min-width: 0;
`;

const StepTitle = styled('div')<{ completed?: boolean }>`
  font-size: ${(p) => (p.completed ? '12px' : '13px')};
  font-weight: ${(p) => (p.completed ? '400' : '600')};
  color: ${(p) => (p.completed ? '#94a3b8' : '#0e1c2b')};
  margin-bottom: 10px;
`;

const IndentedContent = styled('div')`
  min-width: 0;
`;

const UrlField = styled('div')`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 11.5px;
  color: #4f4f4f;
  background: #fff;
  word-break: break-all;
  min-width: 0;
`;

const DimensionRows = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const DimensionRow = styled('div')`
  display: grid;
  grid-template-columns: 1fr 1.4fr auto;
  gap: 8px;
  align-items: center;
  min-width: 0;
`;

const SelectLookalike = styled('div')`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 11.5px;
  color: #4f4f4f;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
  gap: 6px;
`;

const SelectLabel = styled('span')`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ColorDot = styled('span')<{ color: string }>`
  flex-shrink: 0;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(p) => p.color};
`;

const RowAffordance = styled('div')`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const ActionsRow = styled('div')`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 14px;
  gap: 12px;
`;

const CreateLinksButton = styled('div')`
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  color: #0e1c2b;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 7px 14px;
`;

const StepSection = styled('div')`
  border-top: 1px solid #f1f5f9;
  margin-top: 16px;
  padding-top: 14px;
`;

const IntegrationsHeader = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const IntegrationsLabel = styled('span')`
  font-size: 11px;
  color: #64748b;
`;

const CompareRates = styled('span')`
  font-size: 11px;
  color: ${COLORS.PRIMARY};
`;

const IntegrationRow = styled('div')`
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 9px 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const IntegrationName = styled('span')`
  font-size: 12px;
  font-weight: 600;
  color: #0e1c2b;
`;

const FooterRow = styled('div')`
  display: flex;
  justify-content: flex-end;
  border-top: 1px solid #f1f5f9;
  margin-top: 16px;
  padding-top: 14px;
`;

const CopyButton = styled('div')`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 12.5px;
  font-weight: 600;
  color: #fff;
  background: ${COLORS.PRIMARY};
  border-radius: 8px;
  padding: 9px 16px;
`;

export const NewsletterLinkMockup = () => (
  <ProductMockupFrame aria-hidden>
    <AppMockupChrome product="Amplify" surface="Link generator · Newsletter" />
    <ProductMockupBody style={{ background: '#fff' }}>
      <TitleRow>
        <TitleIcon>N</TitleIcon>
        <TitleText>Herschel Supply Co. Novel Recycled Nylon Duff…</TitleText>
      </TitleRow>

      <StepRow>
        <StepBadge completed>
          <Check size={11} />
        </StepBadge>
        <StepContent>
          <StepTitle completed>Add your link</StepTitle>
          <IndentedContent>
            <UrlField>
              https://www.shopmoment.com/products/sierra-backpack
            </UrlField>
          </IndentedContent>
        </StepContent>
      </StepRow>

      <StepRow style={{ marginTop: 14 }}>
        <StepBadge>2</StepBadge>
        <StepContent>
          <StepTitle>Customize your link</StepTitle>
          <IndentedContent>
            <DimensionRows>
              <DimensionRow>
                <SelectLookalike>
                  <span>Channel</span>
                  <ChevronDown size={12} color="#94a3b8" />
                </SelectLookalike>
                <SelectLookalike>
                  <SelectLabel>
                    <ColorDot color="#7fa3ff" />
                    Daily Newsletter
                  </SelectLabel>
                  <ChevronDown size={12} color="#94a3b8" />
                </SelectLookalike>
                <RowAffordance>
                  <Plus size={13} color="#94a3b8" />
                </RowAffordance>
              </DimensionRow>
            </DimensionRows>
            <ActionsRow>
              <CreateLinksButton>Create links</CreateLinksButton>
            </ActionsRow>
          </IndentedContent>
        </StepContent>
      </StepRow>

      <StepSection>
        <StepRow>
          <StepBadge>3</StepBadge>
          <StepContent>
            <StepTitle>Pick the affiliate network</StepTitle>
            <IndentedContent>
              <IntegrationsHeader>
                <IntegrationsLabel>Integrations</IntegrationsLabel>
                <CompareRates>Compare rates</CompareRates>
              </IntegrationsHeader>
              <IntegrationRow>
                <ColorDot color="#7b0051" />
                <IntegrationName>Rakuten</IntegrationName>
                <ChevronDown
                  size={13}
                  color="#94a3b8"
                  style={{ marginLeft: 'auto' }}
                />
              </IntegrationRow>
            </IndentedContent>
          </StepContent>
        </StepRow>
      </StepSection>

      <FooterRow>
        <CopyButton>
          <Copy size={13} />
          Copy link
        </CopyButton>
      </FooterRow>
    </ProductMockupBody>
  </ProductMockupFrame>
);
